import { z } from "zod";

export const jobOfferIdSchema = z.string().regex(/^[0-9a-f]{32}$/u);
export const jobOfferChainIdSchema = jobOfferIdSchema;
export const jobOfferParticipantSideSchema = z.enum(["CUSTOMER", "PROVIDER"]);
export const jobOfferActorSideSchema = z.enum(["CUSTOMER", "PROVIDER", "SYSTEM"]);
export const jobOfferChainStatusSchema = z.enum([
  "OPEN",
  "ACCEPTED",
  "DECLINED",
  "WITHDRAWN",
  "EXPIRED",
  "CLOSED_COMPETING",
]);
export const jobOfferActionCodeSchema = z.enum(["ACCEPT", "DECLINE", "WITHDRAW", "EXPIRE"]);
export const jobOfferIdempotencyKeySchema = z
  .string()
  .min(1)
  .max(256)
  .refine((value) => value === value.trim(), "Idempotency key must not have outer whitespace")
  .regex(/^[\x21-\x7e]+$/u, "Idempotency key must use visible ASCII characters");

const isoDateTimeSchema = z.string().datetime({ offset: true });
const versionNoSchema = z.number().int().min(1);
const timezoneRegionSchema = z
  .string()
  .min(1)
  .max(64)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat("en", { timeZone: value }).format();
      return true;
    } catch {
      return false;
    }
  }, "Use a valid timezone region");

function timestampOffset(value: string): string | null {
  const offset = /(Z|[+-]\d{2}:\d{2})$/u.exec(value)?.[1];
  return offset === "Z" ? "+00:00" : (offset ?? null);
}

function timezoneOffsetAt(value: string, timezoneRegion: string): string | null {
  const timestamp = new Date(value);
  if (!Number.isFinite(timestamp.getTime())) return null;
  try {
    const timezoneName = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneRegion,
      timeZoneName: "longOffset",
    })
      .formatToParts(timestamp)
      .find(({ type }) => type === "timeZoneName")?.value;
    if (timezoneName === undefined) return null;
    if (timezoneName === "GMT") return "+00:00";
    const offset = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/u.exec(timezoneName);
    if (offset === null) return null;
    return `${offset[1]}${offset[2]?.padStart(2, "0")}:${offset[3] ?? "00"}`;
  } catch {
    return null;
  }
}

function addScheduleIssues(
  value: {
    proposedStartAt: string;
    proposedEndAt: string;
    timezoneRegion: string;
  },
  context: z.RefinementCtx,
): void {
  if (Date.parse(value.proposedEndAt) <= Date.parse(value.proposedStartAt)) {
    context.addIssue({
      code: "custom",
      path: ["proposedEndAt"],
      message: "Offer end must follow offer start",
    });
  }
  if (
    timestampOffset(value.proposedStartAt) !==
    timezoneOffsetAt(value.proposedStartAt, value.timezoneRegion)
  ) {
    context.addIssue({
      code: "custom",
      path: ["proposedStartAt"],
      message: "Offer start offset must match its timezone",
    });
  }
  if (
    timestampOffset(value.proposedEndAt) !==
    timezoneOffsetAt(value.proposedEndAt, value.timezoneRegion)
  ) {
    context.addIssue({
      code: "custom",
      path: ["proposedEndAt"],
      message: "Offer end offset must match its timezone",
    });
  }
}

export const jobOfferRevisionSchema = z
  .strictObject({
    offerId: jobOfferIdSchema,
    parentOfferId: jobOfferIdSchema.nullable(),
    revisionNo: z.number().int().min(1).max(20),
    createdBySide: jobOfferParticipantSideSchema,
    currency: z.literal("USD"),
    amountMinor: z.number().int().min(100).max(1_000_000),
    proposedStartAt: isoDateTimeSchema,
    proposedEndAt: isoDateTimeSchema,
    timezoneRegion: timezoneRegionSchema,
    expiresAt: isoDateTimeSchema,
    isCurrent: z.boolean(),
    createdAt: isoDateTimeSchema,
  })
  .superRefine((offer, context) => {
    addScheduleIssues(offer, context);
    const expectedCreator = offer.revisionNo % 2 === 1 ? "PROVIDER" : "CUSTOMER";
    if (offer.createdBySide !== expectedCreator) {
      context.addIssue({
        code: "custom",
        path: ["createdBySide"],
        message: "Offer creators must alternate from the provider",
      });
    }
    if ((offer.revisionNo === 1) !== (offer.parentOfferId === null)) {
      context.addIssue({
        code: "custom",
        path: ["parentOfferId"],
        message: "Offer parent must match its revision",
      });
    }
    const expiryDuration = Date.parse(offer.expiresAt) - Date.parse(offer.createdAt);
    if (expiryDuration < 15 * 60_000 || expiryDuration > 7 * 24 * 60 * 60_000) {
      context.addIssue({
        code: "custom",
        path: ["expiresAt"],
        message: "Offer expiry must be 15 minutes through 7 days after creation",
      });
    }
  });

export const jobOfferActionSchema = z
  .strictObject({
    offerId: jobOfferIdSchema,
    actionCode: jobOfferActionCodeSchema,
    reasonCode: z.string().min(1).max(50).nullable(),
    actorSide: jobOfferActorSideSchema,
    actionAt: isoDateTimeSchema,
  })
  .superRefine((action, context) => {
    if ((action.actionCode === "EXPIRE") !== (action.actorSide === "SYSTEM")) {
      context.addIssue({
        code: "custom",
        path: ["actorSide"],
        message: "Only the system may expire an offer",
      });
    }
  });

const terminalStatusAction: Readonly<
  Partial<
    Record<z.infer<typeof jobOfferChainStatusSchema>, z.infer<typeof jobOfferActionCodeSchema>>
  >
> = {
  ACCEPTED: "ACCEPT",
  DECLINED: "DECLINE",
  WITHDRAWN: "WITHDRAW",
  EXPIRED: "EXPIRE",
};

export const jobOfferChainSchema = z
  .strictObject({
    chainId: jobOfferChainIdSchema,
    jobId: jobOfferIdSchema,
    providerPublicName: z.string().min(1).max(200).nullable(),
    chainStatus: jobOfferChainStatusSchema,
    chainVersionNo: z.number().int().min(1).max(21),
    currentOfferId: jobOfferIdSchema,
    offers: z.array(jobOfferRevisionSchema).min(1).max(20),
    actions: z.array(jobOfferActionSchema).max(1),
  })
  .superRefine((chain, context) => {
    const offerIds = new Set<string>();
    chain.offers.forEach((offer, index) => {
      const expectedRevisionNo = index + 1;
      if (offer.revisionNo !== expectedRevisionNo) {
        context.addIssue({
          code: "custom",
          path: ["offers", index, "revisionNo"],
          message: "Offer revisions must be contiguous and ordered",
        });
      }
      if (offerIds.has(offer.offerId)) {
        context.addIssue({
          code: "custom",
          path: ["offers", index, "offerId"],
          message: "Offer IDs must be unique in a chain",
        });
      }
      offerIds.add(offer.offerId);
      if (index > 0 && offer.parentOfferId !== chain.offers[index - 1]?.offerId) {
        context.addIssue({
          code: "custom",
          path: ["offers", index, "parentOfferId"],
          message: "Each counter must reference the preceding offer",
        });
      }
    });

    const latestOffer = chain.offers.at(-1);
    if (latestOffer === undefined || latestOffer.offerId !== chain.currentOfferId) {
      context.addIssue({
        code: "custom",
        path: ["currentOfferId"],
        message: "Current offer ID must identify the latest revision",
      });
      return;
    }

    const currentOffers = chain.offers.filter(({ isCurrent }) => isCurrent);
    if (chain.chainStatus === "OPEN") {
      if (currentOffers.length !== 1 || currentOffers[0]?.offerId !== latestOffer.offerId) {
        context.addIssue({
          code: "custom",
          path: ["offers"],
          message: "An open chain must have exactly one current latest offer",
        });
      }
      if (chain.actions.length !== 0) {
        context.addIssue({
          code: "custom",
          path: ["actions"],
          message: "An open chain cannot have a terminal action",
        });
      }
    } else if (currentOffers.length !== 0) {
      context.addIssue({
        code: "custom",
        path: ["offers"],
        message: "A closed chain cannot have a current offer",
      });
    }

    const expectedVersion = chain.offers.length + (chain.chainStatus === "OPEN" ? 0 : 1);
    if (chain.chainVersionNo !== expectedVersion) {
      context.addIssue({
        code: "custom",
        path: ["chainVersionNo"],
        message: "Chain version must match its immutable history",
      });
    }

    const expectedAction = terminalStatusAction[chain.chainStatus];
    if (expectedAction === undefined) {
      if (chain.chainStatus === "CLOSED_COMPETING" && chain.actions.length !== 0) {
        context.addIssue({
          code: "custom",
          path: ["actions"],
          message: "A competing closure must not fabricate a participant action",
        });
      }
      return;
    }

    const action = chain.actions[0];
    if (
      action === undefined ||
      action.actionCode !== expectedAction ||
      action.offerId !== latestOffer.offerId
    ) {
      context.addIssue({
        code: "custom",
        path: ["actions"],
        message: "Terminal action must match the chain result and latest offer",
      });
      return;
    }

    const recipientSide = latestOffer.createdBySide === "PROVIDER" ? "CUSTOMER" : "PROVIDER";
    const authorizedActor =
      action.actionCode === "WITHDRAW"
        ? latestOffer.createdBySide
        : action.actionCode === "EXPIRE"
          ? "SYSTEM"
          : recipientSide;
    if (action.actorSide !== authorizedActor) {
      context.addIssue({
        code: "custom",
        path: ["actions", 0, "actorSide"],
        message: "Terminal action actor is not authorized for the current offer",
      });
    }
  });

export const jobOfferListResponseSchema = z
  .strictObject({
    status: z.literal("ok"),
    jobId: jobOfferIdSchema,
    jobVersionNo: versionNoSchema,
    serverNow: isoDateTimeSchema,
    chains: z.array(jobOfferChainSchema).max(50),
  })
  .superRefine((response, context) => {
    const chainIds = new Set<string>();
    response.chains.forEach((chain, index) => {
      if (chain.jobId !== response.jobId) {
        context.addIssue({
          code: "custom",
          path: ["chains", index, "jobId"],
          message: "Offer chain must belong to the response job",
        });
      }
      if (chainIds.has(chain.chainId)) {
        context.addIssue({
          code: "custom",
          path: ["chains", index, "chainId"],
          message: "Offer chain IDs must be unique",
        });
      }
      chainIds.add(chain.chainId);
    });
  });

export const createJobOfferInputSchema = z
  .strictObject({
    chainId: jobOfferChainIdSchema.nullable(),
    jobVersionNo: versionNoSchema,
    chainVersionNo: versionNoSchema.nullable(),
    amountMinor: z.number().int().min(100).max(1_000_000),
    proposedStartAt: isoDateTimeSchema,
    proposedEndAt: isoDateTimeSchema,
    timezoneRegion: timezoneRegionSchema,
    expiresAt: isoDateTimeSchema,
  })
  .superRefine((input, context) => {
    addScheduleIssues(input, context);
    if ((input.chainId === null) !== (input.chainVersionNo === null)) {
      context.addIssue({
        code: "custom",
        path: ["chainVersionNo"],
        message: "Chain ID and version must either both be present or both be null",
      });
    }
  });

export const createJobOfferResponseSchema = z
  .strictObject({
    status: z.enum(["created", "existing"]),
    jobId: jobOfferIdSchema,
    chainId: jobOfferChainIdSchema,
    offerId: jobOfferIdSchema,
    revisionNo: z.number().int().min(1).max(20),
    chainVersionNo: z.number().int().min(1).max(20),
  })
  .superRefine((response, context) => {
    if (response.chainVersionNo !== response.revisionNo) {
      context.addIssue({
        code: "custom",
        path: ["chainVersionNo"],
        message: "A created open revision must advance the chain version once",
      });
    }
  });

export const jobOfferActionInputSchema = z.strictObject({
  jobId: jobOfferIdSchema,
  chainId: jobOfferChainIdSchema,
  jobVersionNo: versionNoSchema,
  chainVersionNo: versionNoSchema,
});

const jobOfferActionResponseFields = {
  jobId: jobOfferIdSchema,
  chainId: jobOfferChainIdSchema,
  offerId: jobOfferIdSchema,
} as const;

export const acceptJobOfferResponseSchema = z.strictObject({
  status: z.enum(["accepted", "existing"]),
  ...jobOfferActionResponseFields,
  bookingId: jobOfferIdSchema,
  jobVersionNo: versionNoSchema,
});

export const declineJobOfferResponseSchema = z.strictObject({
  status: z.enum(["declined", "existing"]),
  ...jobOfferActionResponseFields,
  actionId: jobOfferIdSchema,
});

export const withdrawJobOfferResponseSchema = z.strictObject({
  status: z.enum(["withdrawn", "existing"]),
  ...jobOfferActionResponseFields,
  actionId: jobOfferIdSchema,
});

export type JobOfferParticipantSide = z.infer<typeof jobOfferParticipantSideSchema>;
export type JobOfferActorSide = z.infer<typeof jobOfferActorSideSchema>;
export type JobOfferChainStatus = z.infer<typeof jobOfferChainStatusSchema>;
export type JobOfferActionCode = z.infer<typeof jobOfferActionCodeSchema>;
export type JobOfferRevision = z.infer<typeof jobOfferRevisionSchema>;
export type JobOfferAction = z.infer<typeof jobOfferActionSchema>;
export type JobOfferChain = z.infer<typeof jobOfferChainSchema>;
export type JobOfferListResponse = z.infer<typeof jobOfferListResponseSchema>;
export type CreateJobOfferInput = z.infer<typeof createJobOfferInputSchema>;
export type CreateJobOfferResponse = z.infer<typeof createJobOfferResponseSchema>;
export type JobOfferActionInput = z.infer<typeof jobOfferActionInputSchema>;
export type AcceptJobOfferResponse = z.infer<typeof acceptJobOfferResponseSchema>;
export type DeclineJobOfferResponse = z.infer<typeof declineJobOfferResponseSchema>;
export type WithdrawJobOfferResponse = z.infer<typeof withdrawJobOfferResponseSchema>;
